import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ReportsCenterData } from '../reports-center.data';
import { ServicesData } from '../../services/services.data';

export interface ReportCenterColumnOption {
  key: string;
  title: string;
  selected: boolean;
}

export interface ReportCenterFieldOption {
  value: any;
  title: string;
}

export interface ReportCenterFieldDefinition {
  key: string;
  type: 'multiSelect' | 'select' | 'dateRange' | 'amountRange' | 'autoComplete' | 'checkbox' | 'text' | 'number';
  label?: string;
  placeholder?: string;
  options?: ReportCenterFieldOption[];
  listKey?: string;
  keyProperty?: string;
  titleProperty?: string;
  controllerName?: string;
  idControlKey?: string;
  nameControlKey?: string;
}

export interface ReportCenterReportDefinition {
  key: string;
  title: string;
  description: string;
  icon: string;
  excelFileName: string;
  filters: any;
  fields: ReportCenterFieldDefinition[];
  columns?: ReportCenterColumnOption[];
}

@Component({
  selector: 'report-center-dialog',
  templateUrl: './report-center-dialog.tmpl.html',
  styleUrls: ['./report-center-dialog.scss']
})
export class ReportCenterDialogComponent implements OnInit {
  public formData: FormGroup;
  public columnsControl = new FormControl([]);
  public readonly storageKey: string;

  constructor(
    public dialogRef: MatDialogRef<ReportCenterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReportCenterReportDefinition,
    private formBuilder: FormBuilder,
    public servicesData: ServicesData,
    private reportsCenterData: ReportsCenterData
  ) {
    this.storageKey = `reports-center:${data.key}:columns`;
  }

  ngOnInit(): void {
    this.formData = this.formBuilder.group(this.data.filters);
    this.restoreColumns();
    this.columnsControl.valueChanges.subscribe(value => {
      sessionStorage.setItem(this.storageKey, JSON.stringify(value || []));
    });
  }

  public exportExcel(): void {
    if (!this.formData.valid || !this.data.columns || !this.data.columns.length || !this.columnsControl.value || !this.columnsControl.value.length) {
      this.columnsControl.markAsTouched();
      return;
    }

    this.reportsCenterData.exportReportExcel({
      reportKey: this.data.key,
      includeColumns: (this.columnsControl.value || []).join(','),
      includeAllColumns: false,
      filters: this.formData.value
    }).subscribe(blob => {
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `${this.data.excelFileName}.xlsx`;
      link.click();
      this.dialogRef.close(true);
    });
  }

  public toggleAllColumns(): void {
    if (!this.data.columns || !this.data.columns.length) {
      return;
    }

    if ((this.columnsControl.value || []).length === this.data.columns.length) {
      this.columnsControl.setValue([]);
      return;
    }

    this.columnsControl.setValue(this.data.columns.map(column => column.key));
  }

  public isAllColumnsSelected(): boolean {
    if (!this.data.columns || !this.data.columns.length) {
      return false;
    }

    return (this.columnsControl.value || []).length === this.data.columns.length;
  }

  private restoreColumns(): void {
    if (!this.data.columns || !this.data.columns.length) {
      this.columnsControl.setValue([]);
      return;
    }

    const cachedColumns = sessionStorage.getItem(this.storageKey);
    const defaultColumns = this.data.columns.filter(column => column.selected).map(column => column.key);

    if (!cachedColumns) {
      this.columnsControl.setValue(defaultColumns);
      return;
    }

    try {
      const parsed = JSON.parse(cachedColumns) || [];
      const validColumns = parsed.filter((columnKey: string) => this.data.columns.some(column => column.key === columnKey));
      this.columnsControl.setValue(validColumns.length ? validColumns : defaultColumns);
    } catch {
      this.columnsControl.setValue(defaultColumns);
    }
  }
}
